import React, { useState, useEffect } from "react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Groups as GroupsIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from "@mui/icons-material";
import { userService } from "@/services/userService";
import type { User } from "@/types/auth";

/**
 * Tabla de usuarios con CRUD completo - DESKTOP ONLY
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Componentes con size="small"
 * - dense={true} en tabla
 * - NINGÚN código responsive - desktop only
 * - Máximo 500 líneas
 */

interface UserTableProps {
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onAssignGroups: (user: User) => void;
  onToggleStatus: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  onEdit,
  onDelete,
  onAssignGroups,
  onToggleStatus,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });

  // Cargar usuarios
  const loadUsers = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const data = await userService.getAllUsers(1, 50); // Cargar primera página con 50 usuarios
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (error) {
      console.error("Error cargando usuarios:", error);
      setError(error instanceof Error ? error.message : "Error desconocido");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar usuarios al montar el componente
  useEffect(() => {
    loadUsers();
  }, []);

  // Confirmar eliminación
  const handleDeleteClick = (user: User): void => {
    setDeleteDialog({ open: true, user });
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteDialog.user) return;

    try {
      await onDelete(deleteDialog.user);
      setDeleteDialog({ open: false, user: null });
      await loadUsers(); // Recargar lista
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      setError(
        error instanceof Error ? error.message : "Error eliminando usuario",
      );
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialog({ open: false, user: null });
  };

  // Si hay error de carga inicial, mostrar mensaje con opción de reintento
  if (error && users.length === 0) {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={loadUsers}>
            Reintentar
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Error de acciones */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabla de usuarios */}
      <TableContainer>
        <Table size="small" aria-label="tabla de usuarios">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Último Login</TableCell>
              <TableCell>Fecha Creación</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Stack spacing={2}>
                    <CircularProgress size={48} />
                    <Typography variant="body2" color="text.secondary">
                      Cargando usuarios...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Stack spacing={2}>
                    <Typography variant="h6" color="text.secondary">
                      No hay usuarios registrados
                    </Typography>
                    <Typography variant="body2">
                      No se encontraron usuarios en el sistema
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {user.nombreCompleto ||
                        `${user.nombre || ""} ${user.apellido || ""}`.trim() ||
                        user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.telefono || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.activo ? "Activo" : "Inactivo"}
                      size="small"
                      color={user.activo ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.ultimo_login
                        ? new Date(user.ultimo_login).toLocaleDateString(
                            "es-ES",
                          )
                        : "Nunca"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(user.fecha_creacion).toLocaleDateString(
                        "es-ES",
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Editar usuario">
                        <IconButton size="small" onClick={() => onEdit(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Asignar grupos">
                        <IconButton
                          size="small"
                          onClick={() => onAssignGroups(user)}
                        >
                          <GroupsIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.activo ? "Desactivar" : "Activar"}>
                        <IconButton
                          size="small"
                          onClick={() => onToggleStatus(user)}
                        >
                          {user.activo ? <InactiveIcon /> : <ActiveIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar usuario">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(user)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar al usuario{" "}
            <strong>{deleteDialog.user?.nombreCompleto}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            *Eliminados de pantalla principal
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} size="small">
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            size="small"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
